import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBookingApi, uploadPaymentProofApi, submitReceiptTextApi, startProofUploadApi, getPublicFacilityRulesApi } from '../lib/api';
import { formatTimeRange, formatDate, formatPrice, formatDuration } from '../lib/format';
import { MapPin, Clock, Calendar, Phone, Check, Upload, ArrowLeft, CalendarPlus, Loader2, AlertCircle, Copy, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Countdown timer component
function CountdownTimer({ expiresAt, onExpire }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);
  
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = Math.max(0, expiry - now);
      
      if (diff <= 0 && !expired) {
        setExpired(true);
        setTimeLeft('Expired');
        onExpire?.();
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${mins}:${String(secs).padStart(2, '0')}`);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, expired, onExpire]);
  
  return <span className="font-mono font-bold">{timeLeft}</span>;
}

// Determine current flow state
function getFlowState(booking) {
  if (!booking) return 'loading';
  
  const status = booking.status;
  const paymentStatus = booking.payment_status;
  
  // Terminal states
  if (status === 'expired' || status === 'cancelled') return 'terminal';
  
  // Confirmed state
  if (status === 'confirmed' || paymentStatus === 'paid') return 'confirmed';
  
  // Payment submitted state (proof uploaded, waiting for approval)
  if (status === 'pending_approval' || paymentStatus === 'proof_submitted') return 'submitted';
  
  // Court held state (waiting for payment/upload)
  return 'pending';
}

export default function BookingStatus() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [facilityRules, setFacilityRules] = useState(null);
  const [receiptText, setReceiptText] = useState('');
  const [parsedReceipt, setParsedReceipt] = useState(null);
  const [submittingReceipt, setSubmittingReceipt] = useState(false);

  const loadBooking = useCallback(async () => {
    try {
      const res = await getBookingApi(bookingId);
      setBooking(res.data);
      setError(null);
    } catch (err) {
      setError('Booking not found');
    }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => {
    loadBooking();
    // Auto-refresh every 10 seconds
    const interval = setInterval(loadBooking, 10000);
    return () => clearInterval(interval);
  }, [loadBooking]);

  // Load facility rules for GCash details
  useEffect(() => {
    const loadRules = async () => {
      try {
        const res = await getPublicFacilityRulesApi();
        setFacilityRules(res.data);
      } catch (err) {
        console.error('Failed to load facility rules');
      }
    };
    loadRules();
  }, []);

  const handleUploadProof = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/jpg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      setUploadError(null);
      try {
        await startProofUploadApi(bookingId).catch(() => {});
        await uploadPaymentProofApi(bookingId, file);
        await loadBooking();
      } catch (err) {
        setUploadError(err.response?.data?.detail || 'Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const parseReceiptLocally = (text) => {
    if (!text.trim()) return null;
    const parsed = {};
    const amtMatch = text.match(/₱([\d,]+\.?\d*)/);
    if (amtMatch) parsed.amount = amtMatch[1];
    const refMatch = text.match(/Ref\.?\s*No\.?\s*(\d+)/i);
    if (refMatch) parsed.reference = refMatch[1];
    const tsMatch = text.match(/([A-Z][a-z]{2}\s+\d{1,2},?\s+\d{4}\s+\d{1,2}:\d{2}\s*[AP]M)/i);
    if (tsMatch) parsed.timestamp = tsMatch[1];
    return (parsed.amount || parsed.reference) ? parsed : null;
  };

  const handleReceiptChange = (text) => {
    setReceiptText(text);
    setParsedReceipt(parseReceiptLocally(text));
  };

  const handleSubmitReceipt = async () => {
    if (!receiptText.trim()) return;
    setSubmittingReceipt(true);
    setUploadError(null);
    try {
      await startProofUploadApi(bookingId).catch(() => {});
      await submitReceiptTextApi(bookingId, receiptText);
      await loadBooking();
      toast.success('Receipt submitted!');
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmittingReceipt(false);
    }
  };

  const flowState = getFlowState(booking);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900 mb-2">Booking not found</p>
          <p className="text-sm text-gray-500 mb-6">This booking may have expired or the link is incorrect.</p>
          <Button 
            onClick={() => navigate('/')} 
            className="rounded-full px-8"
            style={{ backgroundColor: '#0F8F8D' }}
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Color tokens
  const colors = {
    amber: { bg: 'rgba(244,166,42,0.12)', border: '#F4A62A', text: '#C4841A', icon: '#F4A62A' },
    softTeal: { bg: 'rgba(15,143,141,0.08)', border: 'rgba(15,143,141,0.3)', text: '#0F8F8D', icon: '#0F8F8D' },
    teal: { bg: 'rgba(15,143,141,0.12)', border: '#0F8F8D', text: '#0F8F8D', icon: '#0F8F8D' },
    gray: { bg: '#F3F4F6', border: '#D1D5DB', text: '#6B7280', icon: '#9CA3AF' },
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="booking-status-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
            data-testid="status-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900" data-testid="status-page-title">
            {booking?.booking_type === 'coaching' || booking?.coach_name
              ? `Coaching Session with Coach ${(booking.coach_name || '').replace(/^\s*(?:coach\s+)+/i, '').trim()}`
              : 'Booking Status'}
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        
        {/* STATE 1: COURT HELD (Amber) - Pending payment */}
        {flowState === 'pending' && (
          <div>
            <div 
              className="rounded-2xl p-6 text-center"
              style={{ backgroundColor: colors.amber.bg, border: `2px solid ${colors.amber.border}` }}
              data-testid="status-court-held"
            >
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.amber.icon }}
              >
                <Clock className="w-7 h-7 text-white" />
              </div>
              
              <h2 className="text-xl font-bold mb-1" style={{ color: colors.amber.text }}>
                Complete Payment to Confirm Your Booking
              </h2>
              
              {booking?.hold_expires_at && (
                <p className="text-xs mb-2" style={{ color: colors.amber.text }}>
                  Hold expires in <CountdownTimer expiresAt={booking.hold_expires_at} onExpire={loadBooking} />
                </p>
              )}
              
              {booking?.payment_status === 'rejected' && (
                <div className="bg-status-expired-bg border border-status-expired/20 rounded-lg p-3 mb-4 text-left">
                  <p className="text-sm text-status-expired font-medium">Payment proof was rejected</p>
                  <p className="text-xs text-status-expired/70 mt-1">Please upload a clear screenshot or paste your receipt again.</p>
                </div>
              )}
              
              {uploadError && (
                <div className="bg-status-expired-bg border border-status-expired/20 rounded-lg p-3 mb-4 text-sm text-status-expired">
                  {uploadError}
                </div>
              )}
            </div>

            {/* GCash Payment Details */}
            {facilityRules?.gcash_number && (
              <div className="bg-brand-teal-tint rounded-2xl border border-brand-teal/20 p-5 mt-4" data-testid="gcash-payment-info">
                <p className="text-xs font-semibold text-brand-teal uppercase tracking-wider mb-2">Send payment via GCash</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-brand-teal">{facilityRules.gcash_number}</p>
                    <p className="text-sm text-brand-teal">{facilityRules.gcash_account_name || 'Turning Point Pickleball'}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(facilityRules.gcash_number.replace(/\s/g, ''));
                      toast.success('GCash number copied!');
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-teal-tint hover:opacity-80 transition-colors text-xs font-medium text-brand-teal"
                    data-testid="copy-gcash-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
                {facilityRules.gcash_qr_image && (
                  <div className="mt-4 flex justify-center">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${facilityRules.gcash_qr_image}`}
                      alt="GCash QR Code"
                      className="rounded-xl border border-brand-teal/20 max-w-[200px] w-full"
                      data-testid="gcash-qr-code"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Option 1: Upload Screenshot */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-4">
              <p className="text-sm font-semibold text-gray-800 mb-3">Upload Payment Screenshot</p>
              <Button
                onClick={handleUploadProof}
                disabled={uploading || submittingReceipt}
                className="w-full text-white rounded-xl py-3 font-medium bg-brand-teal hover:bg-brand-teal-hover"
                data-testid="upload-proof-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload GCash Screenshot</>
                )}
              </Button>
            </div>

            {/* OR Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Option 2: Paste GCash Receipt */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="text-sm font-semibold text-gray-800 mb-1">Paste GCash Receipt</p>
              <p className="text-xs text-gray-400 mb-3">After sending payment, tap "Share Receipt" in GCash and paste the receipt text below.</p>
              <textarea
                data-testid="receipt-text-input"
                value={receiptText}
                onChange={(e) => handleReceiptChange(e.target.value)}
                placeholder={"You sent ₱700.00\nto 0917 304 3229\nMar 07, 2026 1:17 PM\nRef No. 7038508458312"}
                className="w-full rounded-xl border border-gray-200 p-3 text-sm text-gray-700 placeholder:text-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
                rows={5}
              />
              {parsedReceipt && (
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1" data-testid="parsed-receipt-preview">
                  <p className="text-xs font-semibold text-emerald-800">Detected payment:</p>
                  {parsedReceipt.amount && <p className="text-xs text-emerald-700">Amount: ₱{parsedReceipt.amount}</p>}
                  {parsedReceipt.timestamp && <p className="text-xs text-emerald-700">Time: {parsedReceipt.timestamp}</p>}
                  {parsedReceipt.reference && <p className="text-xs text-emerald-700">Reference: {parsedReceipt.reference}</p>}
                </div>
              )}
              <Button
                data-testid="submit-receipt-btn"
                onClick={handleSubmitReceipt}
                disabled={!receiptText.trim() || submittingReceipt || uploading}
                className="w-full mt-3 text-white rounded-xl py-3 font-medium bg-brand-teal hover:bg-brand-teal-hover"
              >
                {submittingReceipt ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <><FileText className="w-4 h-4 mr-2" /> Submit Receipt</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STATE 2: PAYMENT SUBMITTED (Soft Teal) - Waiting for approval */}
        {flowState === 'submitted' && (
          <div 
            className="rounded-2xl p-6 text-center"
            style={{ backgroundColor: colors.softTeal.bg, border: `1px solid ${colors.softTeal.border}` }}
            data-testid="status-payment-submitted"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${colors.softTeal.icon}20`, border: `2px solid ${colors.softTeal.icon}40` }}
            >
              <Clock className="w-7 h-7" style={{ color: colors.softTeal.icon }} />
            </div>
            
            <h2 className="text-xl font-bold mb-1" style={{ color: colors.softTeal.text }}>
              Payment Submitted
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              We've received your proof. Waiting for approval.
            </p>
            
            <div 
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium"
              style={{ backgroundColor: 'white', color: colors.softTeal.text, border: `1px solid ${colors.softTeal.border}` }}
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Waiting for approval
            </div>
          </div>
        )}

        {/* STATE 3: CONFIRMED (Teal) - Booking complete */}
        {flowState === 'confirmed' && (
          <div 
            className="rounded-2xl p-6 text-center"
            style={{ backgroundColor: colors.teal.bg, border: `2px solid ${colors.teal.border}` }}
            data-testid="status-confirmed"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: colors.teal.icon }}
            >
              <Check className="w-7 h-7 text-white" />
            </div>
            
            <h2 className="text-xl font-bold mb-1" style={{ color: colors.teal.text }}>
              Confirmed
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              See you on court!
            </p>
            
            <Button
              onClick={() => {
                // Create calendar event
                const startDate = new Date(`${booking.date}T${String(booking.start_hour).padStart(2, '0')}:00:00`);
                const endDate = new Date(`${booking.date}T${String(booking.end_hour).padStart(2, '0')}:00:00`);
                const title = encodeURIComponent(`Pickleball - ${booking.court_name}`);
                const details = encodeURIComponent(`Court: ${booking.court_name}`);
                const location = encodeURIComponent('TP Pickleball');
                const dates = `${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
                window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${location}`, '_blank');
              }}
              variant="outline"
              className="rounded-xl py-3 font-medium"
              style={{ borderColor: colors.teal.border, color: colors.teal.text }}
              data-testid="add-to-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Add to calendar
            </Button>
          </div>
        )}

        {/* TERMINAL: EXPIRED/CANCELLED (Gray) */}
        {flowState === 'terminal' && (
          <div 
            className="rounded-2xl p-6 text-center"
            style={{ backgroundColor: colors.gray.bg, border: `1px solid ${colors.gray.border}` }}
            data-testid="status-terminal"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: colors.gray.icon }}
            >
              <AlertCircle className="w-7 h-7 text-white" />
            </div>
            
            <h2 className="text-xl font-bold mb-1 text-gray-900">
              {booking?.status === 'expired' ? 'Hold Expired' : 'Booking Cancelled'}
            </h2>
            <p className="text-sm text-gray-500">
              {booking?.status === 'expired' 
                ? 'The court has been released for others to book.' 
                : 'This booking has been cancelled.'}
            </p>
          </div>
        )}

        {/* Booking Details Card */}
        {booking && flowState !== 'terminal' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid="booking-details-card">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Booking Details</p>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{formatDate(booking.date)}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {formatTimeRange(booking.start_hour, booking.end_hour)} · {formatDuration(booking.duration_hours)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{booking.court_name}</span>
              </div>
              {booking.player_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{booking.player_phone}</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {(booking.booking_type === 'coaching' || booking.coach_name) ? 'Court rental' : 'Amount'}
              </span>
              <span className="text-xl font-bold text-gray-900">{formatPrice(booking.amount)}</span>
            </div>
            
            {booking.discount_amount > 0 && (
              <div className="mt-2 flex justify-between items-center text-sm" data-testid="booking-discount-line">
                <span className="text-gray-400">
                  Discount applied {booking.original_amount ? `(from ${formatPrice(booking.original_amount)})` : ''}
                </span>
                <span className="text-brand-teal font-medium">− {formatPrice(booking.discount_amount)}</span>
              </div>
            )}
            
            {/* Coaching info if applicable */}
            {(booking.booking_type === 'coaching' || booking.coach_name) && (
              <div 
                className="mt-4 rounded-lg px-3 py-2"
                style={{ backgroundColor: 'rgba(14,111,108,0.12)', border: '1px solid rgba(14,111,108,0.3)' }}
                data-testid="coaching-info-card"
              >
                <p className="text-sm" style={{ color: '#0E6F6C' }}>
                  <span className="font-medium">Coach:</span> {(booking.coach_name || '').replace(/^\s*(?:coach\s+)+/i, '').trim()}
                </p>
                <p className="text-xs mt-1 opacity-80" style={{ color: '#0E6F6C' }}>
                  Court rental only — coaching fee is settled directly with your coach.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Back to Home */}
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full rounded-xl py-4 border-gray-200 text-gray-600 hover:bg-gray-50"
          data-testid="status-home-btn"
        >
          Back to Home
        </Button>
      </div>
    </div>
  );
}
